// hooks and context
import useOnce from '@hooks/use-once';
import { cropEmailSignatureImageToGivenShape } from '@modules/email-signatures-helpers';
// api
import {
  createImageElementFromURL,
  downloadImageBlob,
  downloadRemoteImage,
  getDataUrlFromImageBlob,
} from '@modules/image-helpers';
import type { EmailSignaturesActions } from '@reducers/email-signature-reducer';
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
// utils
import produce from 'immer';
import { useCallback, useState } from 'react';

import { EmailSignatureFieldTypes } from '../../../consts';
// types
import type {
  EmailSignatureCrop,
  EmailSignatureImageFieldId,
  EmailSignatureShapes,
  EmailSignatureV2,
  EmailSignaturesImages,
} from '../../../types';
import type EmailSignatureImagesUploader from '../../EmailSignatureImagesUploader';

interface ImageServices {
  localCroppedImagesUrls: Record<EmailSignatureImageFieldId, string>;
  originalImagesUrls: Record<EmailSignatureImageFieldId, string>;
  handleUpdateLocalCroppedImage: (
    newLocalImageUrl: string,
    imageId: EmailSignatureImageFieldId,
    shape: EmailSignatureShapes,
    crop: EmailSignatureCrop | null,
  ) => void;
  handleUpdateImageShape: (
    imageId: EmailSignatureImageFieldId,
    shape: EmailSignatureShapes,
    crop: EmailSignatureCrop | null,
  ) => void;
  handleUploadOriginalImageUrl: (
    imageReference: string,
    imageId: EmailSignatureImageFieldId,
  ) => void;
  displayImagesInViewMode: Record<EmailSignatureImageFieldId, boolean>;
  handleChangeImageViewMode: (
    newDisplayValue: boolean,
    imageId: EmailSignatureImageFieldId,
  ) => void;
}

interface ImageServicesProps {
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  handleOnErrorUploading: (fieldType: EmailSignatureFieldTypes) => void;
  isInTransitionState?: boolean;
  isEditingSavedEmailSignature?: boolean;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
}

/*
    This component is a context wrapper that provides all the core services for the email signature side editor,
    it should be used with the email signature Side Editor component. This component allows SideEditor component to
    be reused in different editors (currently used in EmailSignatureMainEditor and EmailSignatureOnboardingEditor)
*/

function useImageServices({
  emailSignature,
  emailSignatureDispatch,
  emailSignatureHasBeenLoaded,
  isEditingSavedEmailSignature,
  handleOnErrorUploading,
  uploadEmailSignatureFieldImagesThunk,
}: ImageServicesProps): ImageServices {
  const [localCroppedImagesUrls, setLocalCroppedImagesUrls] = useState<
    Record<EmailSignatureImageFieldId, string>
  >({});
  const [originalImagesUrls, setOriginalImagesUrls] = useState<
    Record<EmailSignatureImageFieldId, string>
  >({});
  const [displayImagesInViewMode, setDisplayImagesInViewMode] = useState<
    Record<EmailSignatureImageFieldId, boolean>
  >({});

  // When the user loads the saved email signature, we need to initialise the cropped images references and original image so that
  // we have the image references to work with when the user changes the shape of the images.
  useOnce(
    (preventSecondRun) => {
      async function initialiseImages(images: EmailSignaturesImages) {
        await Promise.all(
          images.map(async (image) => {
            const croppedBlob = await downloadRemoteImage(image.imageUrl);
            const originalImageBlob = await downloadRemoteImage(image.originalImageUrl);

            const croppedDataUrl = croppedBlob && (await getDataUrlFromImageBlob(croppedBlob));
            const originalImageDataUrl =
              originalImageBlob && (await getDataUrlFromImageBlob(originalImageBlob));

            setLocalCroppedImagesUrls(
              produce((draftLocalCroppedImagesUrls) => {
                draftLocalCroppedImagesUrls[image.id] = croppedDataUrl || '';
              }),
            );
            setOriginalImagesUrls(
              produce((draftOriginalUrls) => {
                draftOriginalUrls[image.id] = originalImageDataUrl || '';
              }),
            );
          }),
        );
      }

      if (
        !emailSignatureHasBeenLoaded ||
        emailSignature.images.length === 0 ||
        !isEditingSavedEmailSignature
      ) {
        return;
      }

      initialiseImages(emailSignature.images);
      preventSecondRun();
    },
    [emailSignatureHasBeenLoaded, emailSignature.images, isEditingSavedEmailSignature],
  );

  useOnce(
    (preventSecond) => {
      if (
        isEmpty(emailSignature.images) ||
        !emailSignatureHasBeenLoaded ||
        !isEditingSavedEmailSignature
      ) {
        return;
      }

      emailSignature.images.forEach(
        (image) =>
          image.imageUrl &&
          setDisplayImagesInViewMode(
            produce((draftImagesViewMode) => {
              draftImagesViewMode[image.id] = true;
            }),
          ),
      );
      preventSecond();
    },
    [emailSignatureHasBeenLoaded, emailSignature.images, isEditingSavedEmailSignature],
  );

  const handleUpdateLocalCroppedImage = useCallback(
    async (
      newLocalImageUrl: string,
      imageId: EmailSignatureImageFieldId,
      shape: EmailSignatureShapes,
      crop: EmailSignatureCrop | null,
    ) => {
      setLocalCroppedImagesUrls(
        produce((draftLocalCroppedImagesUrls) => {
          draftLocalCroppedImagesUrls[imageId] = newLocalImageUrl;
        }),
      );

      if (!newLocalImageUrl) {
        return;
      }

      const imageElement = await createImageElementFromURL(newLocalImageUrl);

      if (!imageElement) {
        return;
      }

      const croppedEmailSignatureImageUrl = cropEmailSignatureImageToGivenShape(
        shape,
        imageElement,
      );

      if (!croppedEmailSignatureImageUrl) {
        return;
      }

      emailSignatureDispatch({
        type: 'UPDATE_IMAGE_URL',
        value: { id: imageId, imageUrl: croppedEmailSignatureImageUrl, crop },
      });

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          imageId,
          croppedEmailSignatureImageUrl,
          EmailSignatureFieldTypes.IMAGE,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_IMAGE',
              value: imageId,
            });
          },
        );
      });
    },
    [emailSignatureDispatch, uploadEmailSignatureFieldImagesThunk, handleOnErrorUploading],
  );

  const handleUpdateImageShape = useCallback(
    async (
      imageId: EmailSignatureImageFieldId,
      shape: EmailSignatureShapes,
      crop: EmailSignatureCrop | null,
    ) => {
      const imageElement = await createImageElementFromURL(localCroppedImagesUrls[imageId]);

      if (!imageElement) {
        return;
      }

      const croppedEmailSignatureImageUrl = cropEmailSignatureImageToGivenShape(
        shape,
        imageElement,
      );

      if (!croppedEmailSignatureImageUrl) {
        return;
      }

      emailSignatureDispatch({
        type: 'UPDATE_IMAGE_URL',
        value: { id: imageId, imageUrl: croppedEmailSignatureImageUrl, crop },
      });

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          imageId,
          croppedEmailSignatureImageUrl,
          EmailSignatureFieldTypes.IMAGE,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_IMAGE',
              value: imageId,
            });
          },
        );
      });
    },
    [
      localCroppedImagesUrls,
      emailSignatureDispatch,
      uploadEmailSignatureFieldImagesThunk,
      handleOnErrorUploading,
    ],
  );

  const handleUploadOriginalImageUrl = useCallback(
    async (imageReference: string, imageId: EmailSignatureImageFieldId) => {
      setOriginalImagesUrls(
        produce((draftOriginalImagesUrls) => {
          draftOriginalImagesUrls[imageId] = imageReference;
        }),
      );

      if (!imageReference) return;
      const blob = await downloadImageBlob(imageReference);
      const imageDataUrl = await getDataUrlFromImageBlob(blob);
      if (!imageDataUrl) return;

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) =>
        emailSignatureImagesUploader.uploadImage(
          imageId,
          imageDataUrl,
          EmailSignatureFieldTypes.IMAGE,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_IMAGE',
              value: imageId,
            });
          },
          true,
        ),
      );
    },
    [emailSignatureDispatch, uploadEmailSignatureFieldImagesThunk, handleOnErrorUploading],
  );

  const handleChangeImageViewMode = useCallback(
    (newDisplayValue: boolean, imageId: EmailSignatureImageFieldId) => {
      setDisplayImagesInViewMode(
        produce((draftImagesViewMode) => {
          draftImagesViewMode[imageId] = newDisplayValue;
        }),
      );
    },
    [],
  );

  return {
    originalImagesUrls,
    handleUpdateImageShape,
    handleUpdateLocalCroppedImage,
    localCroppedImagesUrls,
    handleUploadOriginalImageUrl,
    displayImagesInViewMode,
    handleChangeImageViewMode,
  };
}

export { useImageServices };
